import ExifParser from 'exif-parser'
import iconRotate from '@/assets/rotate.jpeg'
const ICON_WIDTH = 2 as const
const EXIF_SIZE = 65536 as const
const EXIF_DATE = ['DateTimeOriginal', 'DateTimeDigitized', 'DateTime'] as const

export interface ExifInfo {
  orientation: number
  imageISO: number
  model: string
  date: string
}

export default class ImageUtil {
  private static imageTmp = document.createElement('img')

  private static emptyExif: ExifInfo = {
    orientation: 1,
    imageISO: 0,
    model: '',
    date: ''
  }

  private static padZero (input: number, digit: number): string {
    return input.toString().padStart(digit, '0')
  }

  static checkBrowser (): Promise<boolean> {
    return new Promise((resolve, reject) => {
      ImageUtil
        .loadImageFromFile(null)
        .then(() => {
          if (ImageUtil.imageTmp.width === ICON_WIDTH) {
            resolve(true)
          } else {
            resolve(false)
          }
        }).catch(() => {
          reject(new Error('Fail to load rotate image'))
        })
    })
  }

  private static loadImageFromFile (file: File | null): Promise<void> {
    return new Promise((resolve, reject) => {
      function unbindEvents () {
        ImageUtil.imageTmp.onload = null
        ImageUtil.imageTmp.onerror = null
        ImageUtil.imageTmp.onabort = null
      }

      function loaded () {
        unbindEvents()
        resolve()
      }

      function errored () {
        unbindEvents()
        if (file !== null) {
          URL.revokeObjectURL(ImageUtil.imageTmp.src)
        }
        reject(new Error())
      }

      ImageUtil.imageTmp.onload = loaded
      ImageUtil.imageTmp.onerror = errored
      ImageUtil.imageTmp.onabort = errored
      ImageUtil.imageTmp.setAttribute('crossOrigin', 'anonymous')
      if (file === null) {
        ImageUtil.imageTmp.src = iconRotate
      } else {
        ImageUtil.imageTmp.src = URL.createObjectURL(file)
      }
    })
  }

  static getExif (file: Blob, orientationOnly = false): Promise<ExifInfo> {
    const fileReader = new FileReader()
    const exifPart = file.slice(0, EXIF_SIZE)
    const exifResult = JSON.parse(JSON.stringify(ImageUtil.emptyExif)) as ExifInfo
    return new Promise((resolve) => {
      fileReader.onload = (e): void => {
        const parser = ExifParser.create(e.target?.result, window)
        try {
          const result = parser.parse().tags
          if (result !== undefined) {
            if (result.Orientation !== undefined) {
              exifResult.orientation = result.Orientation
            }
            if (!orientationOnly) {
              if (result.ISO !== undefined) {
                exifResult.imageISO = result.ISO
              }
              if (result.Model !== undefined) {
                exifResult.model = result.Model
              }
              for (let i = 0; i < EXIF_DATE.length; i += 1) {
                if (result[EXIF_DATE[i]] !== undefined) {
                  const date = new Date()
                  date.setTime(result[EXIF_DATE[i]] * 1000)
                  // replace sprintf in the future
                  const year = ImageUtil.padZero(date.getFullYear(), 4)
                  const month = ImageUtil.padZero(date.getMonth() + 1, 2)
                  const day = ImageUtil.padZero(date.getDate(), 2)
                  exifResult.date = `${year}.${month}.${day}`
                  break
                }
              }
            }
          }
          resolve(exifResult)
        } catch (err) {
          // parse error, maybe non-jpg
          resolve(exifResult)
        }
      }
      fileReader.onerror = () => {
        fileReader.abort()
        resolve(exifResult)
      }
      fileReader.readAsArrayBuffer(exifPart)
    })
  }

  static getFileImageByByte (file: File): Promise<Blob> {
    return new Promise((resolve) => {
      const fileReader = new FileReader()
      fileReader.onloadend = function fileReaderLoaded (e): void {
        let type = ''
        const bytes = (new Uint8Array(e.target?.result as ArrayBuffer))
        const header = bytes
          .subarray(0, 4)
          .reduce((prev, curr) => `${prev}${curr.toString(16)}`, '')

        switch (header) {
          case '89504e47':
            type = 'png'
            break
          case '47494638':
            type = 'gif'
            break
          case 'ffd8ffe0':
          case 'ffd8ffe1':
          case 'ffd8ffe2':
          case 'ffd8ffe3':
          case 'ffd8ffe8':
            type = 'jpeg'
            break
          default:
            type = 'unknown'
            break
        }
        resolve(new Blob([bytes], { type: `image/${type}` }))
      }
      fileReader.readAsArrayBuffer(file)
    })
  }
}
